import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SectionCard,
  FormRow,
  PrimaryButton,
  SecondaryButton,
  ConfirmDialog
} from "@/components/ds";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { useAdminOpsTimezone } from "@/hooks/useResolvedTimezone";
import { formatDateInSiteTz } from "@/utils/formatInSiteTz";
import { Severity } from "@velaro/velaro-component-lib";
import useAgentCrmConnection from "./useAgentCrmConnection";

interface DynamicsConfigSummary {
  id: number;
  displayName: string;
  orgName: string;
  enabled: boolean;
  allowPerAgentAttribution: boolean;
}

/**
 * "Connected Accounts" — self-service Dynamics 365 per-agent OAuth attribution.
 * Self-view-only: rendered by UserProfile.tsx only when viewing your own profile.
 *
 * Visible only when the site has exactly one enabled DynamicsConfig with
 * AllowPerAgentAttribution on (server/Velaro.Admin/Controllers/DynamicsController.cs
 * ConnectStart — v1 restricts per-agent attribution to single-org sites).
 */
export default function ConnectedAccountsSection() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const opsTz = useAdminOpsTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState<DynamicsConfigSummary[] | null>(null);
  const [configsError, setConfigsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.admin.get("Dynamics/Configs");
        if (!res.ok) {
          if (!cancelled) {
            setConfigs([]);
            setConfigsError(`Failed to load Dynamics 365 configuration (HTTP ${res.status})`);
          }
          return;
        }
        const data = await res.json();
        if (!cancelled) setConfigs(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          setConfigs([]);
          setConfigsError(
            `Failed to load Dynamics 365 configuration: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api.admin]);

  // Must mirror DynamicsController.ConnectStart's real eligibility check exactly: it counts
  // ALL enabled configs for the site (not just attribution-enabled ones) and 400s unless
  // there is exactly one. Filtering straight to enabled+allowPerAgentAttribution first would
  // show a Connect button on a two-enabled-config site where only one has attribution on —
  // and clicking it would hit that 400.
  const enabledConfigs = (configs ?? []).filter((c) => c.enabled);
  const config =
    enabledConfigs.length === 1 && enabledConfigs[0].allowPerAgentAttribution
      ? enabledConfigs[0]
      : null;

  const { status, loading, error, connecting, disconnecting, connect, disconnect } =
    useAgentCrmConnection(config?.id ?? null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  // Handle the connect/callback redirect's success/failure flag
  // (DynamicsController.ConnectCallback → ?dynamicsConnect=success|error), then scrub it
  // from the URL so a refresh doesn't re-show the toast.
  useEffect(() => {
    const flag = searchParams.get("dynamicsConnect");
    if (!flag) return;
    if (flag === "success") {
      displayToast(Severity.Success, "Dynamics 365 account connected.");
    } else if (flag === "error") {
      displayToast(
        Severity.Error,
        "Couldn't connect your Dynamics 365 account. Please try again, or ask your admin to confirm the connection is enabled."
      );
    }
    // Functional form — never overwrite params some other effect added between mount and
    // this running; only ever remove the one key this effect owns.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("dynamicsConnect");
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (configsError) {
    return (
      <SectionCard title="Connected Accounts" className="mb-4">
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {configsError}
        </div>
      </SectionCard>
    );
  }

  // Not loaded yet, or no eligible (enabled + attribution-enabled, single) config — nothing
  // to show. Per the plan, this section is opt-in per site, not a universal profile fixture.
  if (configs === null || !config) return null;

  return (
    <SectionCard title="Connected Accounts" className="mb-4">
      <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
        Connect your own Microsoft Dynamics 365 account so actions you take get attributed
        to you personally, instead of Velaro's shared connection. Actions taken while a
        conversation is assigned to you may occasionally be automated, not manual — this
        only affects manual actions taken on Dynamics 365.
      </p>

      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400 mb-3">
          {error}
        </div>
      )}

      <div aria-live="polite">
        {loading ? (
          <div className="text-sm text-zinc-600 dark:text-zinc-400">Checking connection status…</div>
        ) : status.connected ? (
          <>
            <FormRow label="Dynamics 365 account" hint={status.connectedEmail ?? undefined}>
              <SecondaryButton
                onClick={() => setConfirmingDisconnect(true)}
                disabled={disconnecting}
                aria-label="Disconnect your Dynamics 365 account"
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </SecondaryButton>
            </FormRow>
            {status.connectedAtUtc && (
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                Connected since{" "}
                {formatDateInSiteTz(status.connectedAtUtc, opsTz, {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
              </p>
            )}
            {status.reconnectRequired && (
              // No nested role here — the parent wrapper already carries aria-live="polite",
              // and a nested role="status" on top of that can double-announce in some screen
              // readers.
              <div className="mt-3 flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
                Reconnect required — we couldn't refresh your Dynamics 365 connection.
                Disconnect and connect again to keep your actions attributed to you.
              </div>
            )}
          </>
        ) : (
          <FormRow
            label="Dynamics 365 account"
            hint={
              error
                ? "Connection status couldn't be loaded — resolve the error above before connecting."
                : "Not connected — actions on Dynamics 365 use Velaro's shared connection until you connect your own account."
            }
          >
            {/* A status-load failure (500) is NOT "not connected" — don't invite a re-run of the
                whole connect flow against a connection that may be perfectly healthy. */}
            {!error && (
              <PrimaryButton onClick={() => connect()} disabled={connecting}>
                {connecting ? "Redirecting to Microsoft…" : "Connect your Dynamics 365 account"}
              </PrimaryButton>
            )}
          </FormRow>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDisconnect}
        title="Disconnect your Dynamics 365 account?"
        message="Actions on Dynamics 365 will go back to using Velaro's shared connection instead of your own account. You can reconnect any time."
        confirmLabel="Disconnect"
        variant="danger"
        busy={disconnecting}
        onConfirm={async () => {
          await disconnect();
          setConfirmingDisconnect(false);
        }}
        onCancel={() => setConfirmingDisconnect(false)}
      />
    </SectionCard>
  );
}
